import React, { useState, useMemo, useEffect } from 'react';
import {
    Button,
    Space,
    Tag,
    Tooltip,
    Modal,
    message,
    Empty
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    StarFilled,
    ShoppingOutlined,
    AppstoreOutlined,
    SettingOutlined
} from '@ant-design/icons';
import { useSales } from '../SalesContext';
import { ProductSection, Product } from '../types';
import ProductFormModal from './ProductFormModal';
import SectionFormModal from './SectionFormModal';
import BreakpointConfigModal from '../commission/BreakpointConfigModal';
import { salesApi } from '../../../services/salesApi';

interface SectionedProductListProps {
    businessArea: 'telecomunicacao' | 'energia' | 'seguranca';
}

const SectionedProductList: React.FC<SectionedProductListProps> = ({ businessArea }) => {
    const {
        sections,
        products,
        deleteSection,
        deleteProduct,
        fetchSections,
        fetchProducts
    } = useSales();

    const [sectionModalVisible, setSectionModalVisible] = useState(false);
    const [productModalVisible, setProductModalVisible] = useState(false);
    const [breakpointModalVisible, setBreakpointModalVisible] = useState(false);
    const [editingSection, setEditingSection] = useState<ProductSection | null>(null);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [targetSectionId, setTargetSectionId] = useState<number | null>(null);
    const [adjustedCommissions, setAdjustedCommissions] = useState<{ [productId: number]: number }>({});
    const [hasActiveBreakpoint, setHasActiveBreakpoint] = useState(false);

    // Fetch adjusted commissions for Telecomunicação and Segurança
    useEffect(() => {
        if (businessArea === 'telecomunicacao' || businessArea === 'seguranca') {
            const fetchAdjusted = async () => {
                try {
                    const data = await salesApi.getAdjustedCommissions(businessArea);
                    const adjustments: { [productId: number]: number } = {};
                    data.products.forEach((p: any) => {
                        if (p.has_adjustment && p.adjusted_commission !== p.base_commission) {
                            adjustments[p.product_id] = p.adjusted_commission;
                        }
                    });
                    setAdjustedCommissions(adjustments);
                    setHasActiveBreakpoint(data.active_breakpoint !== null);
                } catch (error) {
                    console.error('Error fetching adjusted commissions:', error);
                }
            };
            fetchAdjusted();
            // Refresh every 30 seconds
            const interval = setInterval(fetchAdjusted, 30000);
            return () => clearInterval(interval);
        }
    }, [businessArea]);

    const safeSections = Array.isArray(sections) ? sections : [];
    const safeProducts = Array.isArray(products) ? products : [];

    const areaSections = useMemo(() => {
        const filtered = safeSections.filter(s => s.business_area === businessArea);
        
        // Helper to get max commission for a section
        const getMaxCommissionForSection = (sectionId: number) => {
            const sectionProducts = safeProducts.filter(p => p.section_id === sectionId && p.is_active);
            if (sectionProducts.length === 0) return 0;
            const commissions = sectionProducts.map(p => {
                if ((businessArea === 'telecomunicacao' || businessArea === 'seguranca') && hasActiveBreakpoint && adjustedCommissions && adjustedCommissions[p.id] !== undefined) {
                    return adjustedCommissions[p.id];
                }
                return p.commission || 0;
            });
            return Math.max(...commissions);
        };
        
        // Sort by max commission (points) in descending order
        const sorted = [...filtered].sort((a, b) => {
            const aMax = getMaxCommissionForSection(a.id);
            const bMax = getMaxCommissionForSection(b.id);
            // If commissions are equal, maintain original order by section order
            if (aMax === bMax) {
                return a.order - b.order;
            }
            return bMax - aMax; // Descending order (highest first)
        });
        
        return sorted;
    }, [safeSections, businessArea, safeProducts, hasActiveBreakpoint, adjustedCommissions]);

    const productsBySection = useMemo(() => {
        const map = new Map<number, Product[]>();
        areaSections.forEach(s => map.set(s.id, []));

        safeProducts.forEach(p => {
            if (map.has(p.section_id)) {
                map.get(p.section_id)?.push(p);
            }
        });
        
        // Sort products within each section by commission (lowest to highest)
        map.forEach((products, sectionId) => {
            products.sort((a, b) => {
                const aCommission = businessArea === 'telecomunicacao' && hasActiveBreakpoint && adjustedCommissions[a.id] 
                    ? adjustedCommissions[a.id] 
                    : (a.commission || 0);
                const bCommission = businessArea === 'telecomunicacao' && hasActiveBreakpoint && adjustedCommissions[b.id]
                    ? adjustedCommissions[b.id]
                    : (b.commission || 0);
                return aCommission - bCommission; // Ascending order (lowest first)
            });
        });
        
        return map;
    }, [safeProducts, areaSections, businessArea, hasActiveBreakpoint, adjustedCommissions]);

    const handleEditSection = (section: ProductSection) => {
        setEditingSection(section);
        setSectionModalVisible(true);
    };

    const handleDeleteSection = async (id: number) => {
        Modal.confirm({
            title: 'Eliminar Secção',
            content: 'Tem a certeza? Esta ação irá eliminar também todos os produtos associados.',
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            centered: true,
            onOk: async () => {
                try {
                    await deleteSection(id);
                    message.success('Secção eliminada com sucesso');
                } catch (error) {
                    message.error('Erro ao eliminar secção');
                }
            }
        });
    };

    const handleEditProduct = (product: Product) => {
        setEditingProduct(product);
        setProductModalVisible(true);
    };

    const handleDeleteProduct = async (id: number) => {
        Modal.confirm({
            title: 'Eliminar Produto',
            content: 'Tem a certeza que deseja eliminar este produto?',
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            centered: true,
            onOk: async () => {
                try {
                    await deleteProduct(id);
                    message.success('Produto eliminado com sucesso');
                } catch (error) {
                    message.error('Erro ao eliminar produto');
                }
            }
        });
    };

    const handleAddProduct = (sectionId: number) => {
        setTargetSectionId(sectionId);
        setEditingProduct(null);
        setProductModalVisible(true);
    };

    return (
        <div className="sectioned-product-list">
            <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {(businessArea === 'telecomunicacao' || businessArea === 'seguranca') && (
                    <Button
                        icon={<SettingOutlined />}
                        onClick={() => setBreakpointModalVisible(true)}
                    >
                        Configurar Breakpoints
                    </Button>
                )}
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingSection(null);
                        setSectionModalVisible(true);
                    }}
                    className="add-new-btn"
                >
                    Nova Secção
                </Button>
            </div>

            <BreakpointConfigModal
                visible={breakpointModalVisible}
                onClose={() => setBreakpointModalVisible(false)}
            />

            {areaSections.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <span style={{ color: '#8a95a5' }}>
                            Nenhuma secção criada para esta área de negócio
                        </span>
                    }
                    style={{ padding: '60px 20px' }}
                />
            ) : (
                <div className="sections-grid">
                    {areaSections.map(section => {
                        const sectionProducts = productsBySection.get(section.id) || [];
                        
                        return (
                            <div key={section.id} className="section-card">
                                <div className="section-card-header">
                                    <div className="section-title">
                                        <AppstoreOutlined />
                                        <span>{section.name}</span>
                                        {!section.is_active && (
                                            <Tag color="red" className="modern-tag" style={{ marginLeft: 8 }}>
                                                Inativo
                                            </Tag>
                                        )}
                                    </div>
                                    <div className="action-buttons">
                                        <Tooltip title="Editar">
                                            <Button
                                                type="text"
                                                icon={<EditOutlined />}
                                                onClick={() => handleEditSection(section)}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Eliminar">
                                            <Button
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => handleDeleteSection(section.id)}
                                            />
                                        </Tooltip>
                                    </div>
                                </div>

                                <div className="product-list-container">
                                    {sectionProducts.length === 0 ? (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={
                                                <span style={{ color: '#8a95a5', fontSize: 13 }}>
                                                    Sem produtos nesta secção
                                                </span>
                                            }
                                            style={{ padding: '20px 0' }}
                                        />
                                    ) : (
                                        sectionProducts.map(item => (
                                            <div key={item.id} className="product-list-item">
                                                <div className="product-info">
                                                    <div className={`product-icon-wrapper ${item.highlighted ? 'product-highlight' : ''}`}>
                                                        {item.highlighted ? <StarFilled /> : <ShoppingOutlined />}
                                                    </div>
                                                    <div className="product-details">
                                                        <span className="product-name">
                                                            {item.name}
                                                            {!item.is_active && (
                                                                <Tag color="red" className="modern-tag">
                                                                    Inativo
                                                                </Tag>
                                                            )}
                                                        </span>
                                                        <div className="product-meta">
                                                            {(businessArea === 'telecomunicacao' || businessArea === 'seguranca') && hasActiveBreakpoint && adjustedCommissions[item.id] ? (
                                                                <Space size={4}>
                                                                    <span style={{ 
                                                                        color: '#999', 
                                                                        textDecoration: 'line-through',
                                                                        fontSize: 13
                                                                    }}>
                                                                        {item.commission} Pontos
                                                                    </span>
                                                                    <span style={{ 
                                                                        color: '#ff4d4f', 
                                                                        fontWeight: 700,
                                                                        fontSize: 15
                                                                    }}>
                                                                        {adjustedCommissions[item.id]} Pontos
                                                                    </span>
                                                                </Space>
                                                            ) : (
                                                                <span style={{ color: '#10b981', fontWeight: 600 }}>
                                                                    {item.commission} Pontos
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="action-buttons">
                                                    <Space size={4}>
                                                        <Tooltip title="Editar">
                                                            <Button
                                                                type="text"
                                                                icon={<EditOutlined />}
                                                                onClick={() => handleEditProduct(item)}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title="Eliminar">
                                                            <Button
                                                                type="text"
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => handleDeleteProduct(item.id)}
                                                            />
                                                        </Tooltip>
                                                    </Space>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className="add-product-btn-area">
                                    <Button
                                        block
                                        icon={<PlusOutlined />}
                                        onClick={() => handleAddProduct(section.id)}
                                        className="dashed-btn"
                                    >
                                        Adicionar Produto
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <SectionFormModal
                visible={sectionModalVisible}
                onClose={() => setSectionModalVisible(false)}
                section={editingSection}
                businessArea={businessArea}
                onSuccess={() => {
                    fetchSections(true);
                    setSectionModalVisible(false);
                }}
            />

            <ProductFormModal
                visible={productModalVisible}
                onClose={() => setProductModalVisible(false)}
                product={editingProduct}
                defaultSectionId={targetSectionId}
                onSuccess={() => {
                    fetchProducts(true);
                    setProductModalVisible(false);
                }}
            />
        </div>
    );
};

export default SectionedProductList;
